import { C } from "@hikari/vault-core";

const Para = ({ children }) => (
    <p
        style={{
            color: C.muted,
            fontSize: "0.92rem",
            lineHeight: 1.75,
            margin: "0 0 1rem 0",
            fontFamily: C.font,
        }}
    >
        {children}
    </p>
);

const H = ({ children }) => (
    <h3
        style={{
            color: C.text,
            fontSize: "1.05rem",
            fontWeight: 500,
            margin: "2rem 0 0.75rem",
            fontFamily: C.font,
        }}
    >
        {children}
    </h3>
);

export default function Privacy() {
    return (
        <main
            style={{
                maxWidth: "720px",
                margin: "0 auto",
                padding: "4rem 1.5rem 5rem",
                fontFamily: C.font,
            }}
        >
            <div
                style={{
                    fontSize: "0.72rem",
                    letterSpacing: "0.3em",
                    color: C.accent,
                    marginBottom: "0.75rem",
                }}
            >
                LEGAL
            </div>
            <h1
                style={{
                    fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                    fontWeight: 500,
                    margin: "0 0 0.5rem",
                    color: C.text,
                }}
            >
                Privacy policy
            </h1>
            <div style={{ color: C.dim, fontSize: "0.82rem", marginBottom: "2.5rem" }}>
                Last updated: {new Date().toISOString().slice(0, 10)} · Effective immediately
            </div>

            <Para>
                <strong style={{ color: C.text }}>The short version.</strong> Hikari Vault is a
                zero-knowledge product. We don't ask for your email, name, phone number, or any
                other identifying information when you create a vault. We can't read the contents
                of your vault — it's encrypted on your device before it ever reaches us. This
                document describes the small handful of things we <em>do</em> see, and what we do
                with them.
            </Para>

            <H>1. What we never collect</H>
            <Para>
                We do not collect: your name, email address, phone number, postal address, date of
                birth, government ID, social media handles, contact lists, photographs of your
                face, location data, or any third-party identifier. We do not have any of these on
                file because we never asked for them.
            </Para>

            <H>2. What we do see, and why</H>
            <Para>
                <strong style={{ color: C.text }}>Vault ID.</strong> A deterministic one-way hash
                derived from your recovery phrase. We use it to look up your encrypted blob. It
                cannot be reversed into your phrase. Two different people with the same phrase
                would (by improbable mathematical coincidence) share a vault ID; in practice this
                does not happen.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>Encrypted blob.</strong> An opaque base64 string
                containing your encrypted entries, encrypted trash, and key-wrapping material. We
                cannot decrypt it. To us it is indistinguishable from random noise.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>Timestamps.</strong> When your vault was created
                and last updated. Used so the server knows whether your local cache is fresh.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>WebAuthn credential IDs.</strong> If you enable
                biometric unlock, the public credential ID issued by your device. This is not your
                biometric data — fingerprints and face scans never leave your device.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>IP address (transient).</strong> Your IP reaches
                our server during requests, as it does for any web service. We do not log it to
                disk, do not associate it with your vault ID, and our reverse proxy strips it from
                application logs.
            </Para>

            <H>3. Cookies, tracking, analytics</H>
            <Para>
                We use no cookies for the vault app itself. We run no third-party analytics
                (Google Analytics, Mixpanel, Amplitude, etc.). We use no tracking pixels, no
                fingerprinting libraries, no error-reporting services that transmit user data
                (Sentry, Rollbar, etc.). Marketing pages may use privacy-respecting analytics
                (self-hosted Plausible or similar) — if they do, it will be disclosed on the page.
            </Para>

            <H>4. Payments</H>
            <Para>
                Hikari Vault accepts cryptocurrency payments only. We do not process card payments
                and have no business relationship with Visa, Mastercard, or Stripe. When you pay
                for a Plus licence, your payment passes through our crypto checkout provider
                (BTCPay Server or NOWPayments) and we receive an on-chain transaction. We issue
                you a one-time licence code in exchange. We do not associate the wallet address,
                transaction hash, or licence code with any vault ID.
            </Para>

            <H>5. Third parties</H>
            <Para>
                Our backend runs on infrastructure provided by our cloud host. That host can see
                the same ciphertext we see, plus the IP addresses of incoming requests. We use no
                other third-party data processors. We share nothing with advertising networks,
                data brokers, or marketing partners — because we have nothing to share.
            </Para>

            <H>6. Government requests</H>
            <Para>
                If we receive a valid legal demand (subpoena, warrant, court order) for user data,
                we will produce what we have. What we have for any given user is: an encrypted
                blob, a vault ID hash, and update timestamps. We cannot produce: the contents of
                the vault, the recovery phrase, the master key, the user's identity, or any
                personal information. We will publish an annual transparency report listing the
                number of requests received and what we handed over.
            </Para>

            <H>7. Data deletion</H>
            <Para>
                In Settings, choose “Wipe vault” to immediately delete your local encrypted
                cache and instruct the server to delete its copy. No retention period, no soft
                delete on our side. Once gone, it's gone — we cannot recover it for you and
                cannot be compelled to. Because we hold no PII associated with your vault, there
                are no related records elsewhere in our systems to delete.
            </Para>

            <H>8. Data export</H>
            <Para>
                In Settings, choose “Export vault” to download your full encrypted vault as a
                portable <code>.hvault</code> file. You can decrypt it offline with your recovery
                phrase using our open-source tooling (available with the M5 open-source release).
            </Para>

            <H>9. Children</H>
            <Para>
                Hikari Vault is not directed to children under 13 (or under 16 in jurisdictions
                where that is the applicable age of digital consent). We do not knowingly process
                data belonging to such children. Because we collect no identifying information,
                we cannot verify the age of any individual user.
            </Para>

            <H>10. Changes to this policy</H>
            <Para>
                When we update this policy materially, we will post the new version here with a
                new “last updated” date and, where practical, surface a notice in the app. We
                cannot email you about it — we don't have your email.
            </Para>

            <H>11. Contact</H>
            <Para>
                Questions: <code>privacy@hikarivault.app</code>. Security disclosures:{" "}
                <code>security@hikarivault.app</code> (PGP key published at
                /security on launch).
            </Para>
        </main>
    );
}
